import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Truck,
  Star,
  DollarSign,
  Package,
  CheckCircle,
  Search,
  Loader2,
  AlertCircle,
  X,
  Phone,
  Mail,
  MapPin,
  Calendar,
  TrendingUp,
  Award,
} from "lucide-react";
import toast from "react-hot-toast";
import { adminAPI } from "../../utils/api";

const StatCard = ({ title, value, icon: Icon, color = "blue" }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    yellow: "bg-amber-50 text-amber-600",
    purple: "bg-violet-50 text-violet-600",
  };
  return (
    <div className={`card ${colors[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
        <Icon className="w-8 h-8 opacity-60" />
      </div>
    </div>
  );
};

const AdminCouriers = () => {
  const [search, setSearch] = useState("");
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const queryClient = useQueryClient();

  const {
    data: couriersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["adminCouriers", search],
    queryFn: () => adminAPI.couriers({ search: search || undefined }),
    keepPreviousData: true,
    retry: 2,
  });

  const approveMutation = useMutation({
    mutationFn: (id) => adminAPI.approveCourier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminCouriers"] });
      toast.success("Courier approved successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to approve courier");
    },
  });

  const couriers = couriersData?.data?.data?.data?.data || [];
  const stats = couriersData?.data?.data?.stats || {};

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
        <p className="font-medium">Failed to load couriers</p>
        <button
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ["adminCouriers"] })
          }
          className="mt-3 btn-secondary text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Couriers</h1>
          <p className="text-gray-600 mt-1">Manage your courier fleet</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          title="Total Couriers"
          value={formatNumber(stats.total)}
          icon={Truck}
          color="blue"
        />
        <StatCard
          title="Active"
          value={formatNumber(stats.active)}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title="Pending Approval"
          value={formatNumber(stats.pending)}
          icon={Star}
          color="yellow"
        />
      </div>

      {/* Search */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search couriers by name, email, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-10 w-full"
          />
        </div>
      </div>

      {/* Couriers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {couriers.map((courier) => (
          <div
            key={courier.id}
            className="card hover:shadow-lg transition-shadow duration-200"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mr-3 text-white font-bold text-lg">
                  {courier.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {courier.name}
                  </h3>
                  <p className="text-sm text-gray-500">{courier.email}</p>
                  <p className="text-xs text-gray-400">{courier.phone}</p>
                </div>
              </div>
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                  courier.status === "active"
                    ? "bg-emerald-100 text-emerald-800"
                    : courier.status === "pending"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-rose-100 text-rose-800"
                }`}
              >
                {courier.status}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <Package className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                <p className="text-lg font-semibold text-gray-900">
                  {courier.orders_count || 0}
                </p>
                <p className="text-xs text-gray-500">Deliveries</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <Star className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                <p className="text-lg font-semibold text-gray-900">
                  {courier.rating || "N/A"}
                </p>
                <p className="text-xs text-gray-500">Rating</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <DollarSign className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                <p className="text-lg font-semibold text-gray-900">
                  EGP {formatMoney(courier.earnings || 0)}
                </p>
                <p className="text-xs text-gray-500">Earnings</p>
              </div>
            </div>

            <div className="flex space-x-2">
              {courier.status === "pending" && (
                <button
                  onClick={() => approveMutation.mutate(courier.id)}
                  disabled={approveMutation.isPending}
                  className="flex-1 btn-primary text-sm py-2 disabled:opacity-50 flex items-center justify-center"
                >
                  {approveMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-1" />
                  )}
                  Approve
                </button>
              )}
              <button
                onClick={() => {
                  setSelectedCourier(courier);
                  setShowDetails(true);
                }}
                className="flex-1 btn-secondary text-sm py-2"
              >
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {couriers.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <Truck className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="font-medium">No couriers found</p>
          <p className="text-sm mt-1">Try adjusting your search</p>
        </div>
      )}

      {/* DETAILS MODAL */}
      {showDetails && selectedCourier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">
                Courier Details
              </h2>
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedCourier(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Profile Header */}
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-2xl">
                  {selectedCourier.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {selectedCourier.name}
                  </h3>
                  <span
                    className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium mt-1 ${
                      selectedCourier.status === "active"
                        ? "bg-emerald-100 text-emerald-800"
                        : selectedCourier.status === "pending"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-rose-100 text-rose-800"
                    }`}
                  >
                    {selectedCourier.status}
                  </span>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                  Contact Information
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{selectedCourier.email}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{selectedCourier.phone || "N/A"}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span>
                      {selectedCourier.address || "No address on file"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Performance Stats */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                  Performance
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-xl">
                    <div className="flex items-center space-x-2 mb-1">
                      <Package className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-blue-600 font-medium">
                        Deliveries
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-blue-900">
                      {selectedCourier.orders_count || 0}
                    </p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-xl">
                    <div className="flex items-center space-x-2 mb-1">
                      <Star className="w-4 h-4 text-amber-600" />
                      <span className="text-sm text-amber-600 font-medium">
                        Rating
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-amber-900">
                      {selectedCourier.rating || "N/A"}
                    </p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-xl">
                    <div className="flex items-center space-x-2 mb-1">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-emerald-600 font-medium">
                        Earnings
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-900">
                      EGP {formatMoney(selectedCourier.earnings || 0)}
                    </p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-xl">
                    <div className="flex items-center space-x-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-purple-600" />
                      <span className="text-sm text-purple-600 font-medium">
                        Status
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-purple-900 capitalize">
                      {selectedCourier.status}
                    </p>
                  </div>
                </div>
              </div>

              {/* Account Info */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                  Account
                </h4>
                <div className="flex items-center space-x-3 text-gray-700">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>
                    Joined:{" "}
                    {selectedCourier.created_at
                      ? new Date(
                          selectedCourier.created_at,
                        ).toLocaleDateString()
                      : "N/A"}
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-gray-700">
                  <Award className="w-4 h-4 text-gray-400" />
                  <span>Role: {selectedCourier.role}</span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-100 flex space-x-3">
              {selectedCourier.status === "pending" && (
                <button
                  onClick={() => {
                    approveMutation.mutate(selectedCourier.id);
                    setShowDetails(false);
                  }}
                  disabled={approveMutation.isPending}
                  className="flex-1 btn-primary py-2.5 disabled:opacity-50 flex items-center justify-center"
                >
                  {approveMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Approve Courier
                </button>
              )}
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedCourier(null);
                }}
                className="flex-1 btn-secondary py-2.5"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const formatNumber = (val) => {
  const num =
    typeof val === "string"
      ? parseInt(val, 10)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num) ? "0" : num.toLocaleString("en-US");
};

const formatMoney = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

export default AdminCouriers;
